import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  CreditCard,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function AdminPayments() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [monthlyRevenue, setMonthlyRevenue] = useState(0);
  const [monthlyTransactions, setMonthlyTransactions] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const limit = 20;

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    fetchPayments();
  }, [page]);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payments?skip=${page * limit}&limit=${limit}`);
      setPayments(response.data.payments);
      setTotal(response.data.total);
      setRevenue(response.data.total_revenue);
      setMonthlyRevenue(response.data.monthly_revenue || 0);
      setMonthlyTransactions(response.data.monthly_transactions || 0);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate("/admin");
      }
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-accent" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-destructive" />;
      default:
        return <Clock className="h-4 w-4 text-orange-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-accent';
      case 'failed':
        return 'text-destructive';
      default:
        return 'text-orange-500';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Dashboard
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">PAYMENTS</span>
            </div>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Revenue Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-accent/20 to-transparent border-accent/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Total Revenue</div>
                  <div className="text-3xl font-black text-accent font-mono">${revenue.toFixed(2)}</div>
                </div>
                <DollarSign className="h-10 w-10 text-accent opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-primary/20 to-transparent border-primary/50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">This Month</div>
                  <div className="text-3xl font-black text-primary font-mono">${monthlyRevenue.toFixed(2)}</div>
                </div>
                <CreditCard className="h-10 w-10 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Total Transactions</div>
                  <div className="text-3xl font-black font-mono">{total}</div>
                </div>
                <CreditCard className="h-10 w-10 text-muted-foreground opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground uppercase tracking-wider">Monthly Transactions</div>
                  <div className="text-3xl font-black font-mono">{monthlyTransactions}</div>
                </div>
                <Clock className="h-10 w-10 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payments Table */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="uppercase tracking-tighter flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Transaction History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-secondary/30">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Plan</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Amount</th>
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-muted-foreground">Session ID</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length > 0 ? (
                    payments.map((payment, i) => (
                      <motion.tr
                        key={payment.id || i}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="border-b border-border/50 hover:bg-secondary/20"
                      >
                        <td className="py-3 px-4 text-sm">
                          {new Date(payment.created_at).toLocaleString()}
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-bold uppercase">{payment.plan_id}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-mono font-bold">${payment.amount?.toFixed(2)}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className={`flex items-center justify-center gap-1 ${getStatusColor(payment.payment_status)}`}>
                            {getStatusIcon(payment.payment_status)}
                            <span className="font-bold uppercase text-sm">{payment.payment_status}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs text-muted-foreground font-mono">
                            {payment.session_id?.substring(0, 20)}...
                          </span>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-muted-foreground">
                        No transactions yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-muted-foreground">
              Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-sm"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm">Page {page + 1}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * limit >= total}
                className="rounded-sm"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
